import request from "../../utils/request";
import { getActiveBrandId } from "@eweadn-qiankun/shared-utils";
import {
  setRemoteProductPic,
  getRemoteProductPic,
  isRemoteProductPicExpired,
  normalizeProductPicModelKey,
  type RemoteProductPicColor,
  type RemoteProductPicEntry,
} from "@eweadn-qiankun/shared-store";

type ApiEnvelope<T> = {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: T;
};

export type ProductPicPublicColor = {
  colorKey: string;
  label?: string;
  swatchType?: string;
  swatchValue?: string;
  imageUrl?: string;
  sortOrder?: number;
  isDefault?: boolean;
};

export type ProductPicPublicModel = {
  modelKey: string;
  defaultColorKey?: string;
  /** ISO string or epoch ms */
  expiresAt?: string | number;
  colors?: ProductPicPublicColor[];
};

function assertSuccess<T>(res: unknown): T {
  const body = res as ApiEnvelope<T>;
  if (body?.success === false) {
    throw new Error(body.message || "请求失败");
  }
  if (
    body?.statusCode != null &&
    body.statusCode !== 200 &&
    body.success !== true
  ) {
    throw new Error(body.message || "请求失败");
  }
  return body.data as T;
}

function parseExpiresAt(
  raw: string | number | undefined,
  ttlFallbackMs = 86400000
): number {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw < 1e12 ? raw * 1000 : raw;
  }
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now() + ttlFallbackMs;
}

function toRemoteColors(
  colors: ProductPicPublicColor[] | undefined
): RemoteProductPicColor[] {
  if (!colors?.length) return [];
  return colors
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((c) => ({
      key: c.colorKey,
      label: c.label || c.colorKey,
      image: c.imageUrl || "",
      swatchType: c.swatchType,
      swatchValue: c.swatchValue,
      isDefault: c.isDefault,
      sortOrder: c.sortOrder,
    }));
}

const inflight = new Map<string, Promise<RemoteProductPicEntry>>();

function flightKey(brand: string, modelKey: string) {
  return `${brand.trim().toLowerCase()}::${modelKey.trim()}`;
}

/**
 * 按需拉取型号产品图；同型号并发合并；未过期缓存直接复用。
 */
export async function ensureProductPic(
  brand: string,
  modelKey: string,
  options?: { force?: boolean }
): Promise<RemoteProductPicEntry | undefined> {
  const b =
    (brand || getActiveBrandId() || "eweadn").trim().toLowerCase() || "eweadn";
  const key = normalizeProductPicModelKey(modelKey);
  if (!key) return undefined;

  const cached = getRemoteProductPic(b, key);
  // empty/error 用短缓存，避免测试库刚入库后仍一直命中空结果
  const cacheOk =
    cached &&
    !isRemoteProductPicExpired(cached) &&
    (cached.status === "ok" || !options?.force);
  if (!options?.force && cacheOk && cached.status === "ok") {
    return cached;
  }
  if (
    !options?.force &&
    cached &&
    cached.status !== "ok" &&
    Date.now() < cached.expiresAt
  ) {
    return cached;
  }

  const fk = flightKey(b, key);
  const pending = inflight.get(fk);
  if (pending) return pending;

  const task = (async () => {
    try {
      const res = await request({
        url: `/product-pics/${encodeURIComponent(b)}/models/${encodeURIComponent(key)}`,
        method: "get",
      });
      const data = assertSuccess<ProductPicPublicModel>(res);
      const colors = toRemoteColors(data?.colors);
      const status = colors.length ? "ok" : "empty";
      const entry: RemoteProductPicEntry = {
        brand: b,
        modelKey: data?.modelKey || key,
        defaultColorKey: data?.defaultColorKey,
        // 有图：跟随后端签名 TTL；无图：60s 后再试（便于刚同步完数据）
        expiresAt:
          status === "ok"
            ? parseExpiresAt(data?.expiresAt)
            : Date.now() + 60_000,
        colors,
        status,
      };
      setRemoteProductPic(entry);
      if (typeof console !== "undefined" && console.debug) {
        console.debug(
          `[productPics] ${b}/${key} -> ${entry.status} colors=${colors.length} expiresAt=${entry.expiresAt}`
        );
      }
      return entry;
    } catch (err) {
      if (typeof console !== "undefined" && console.warn) {
        console.warn(`[productPics] fetch failed ${b}/${key}`, err);
      }
      const entry: RemoteProductPicEntry = {
        brand: b,
        modelKey: key,
        expiresAt: Date.now() + 60_000,
        colors: [],
        status: "error",
      };
      setRemoteProductPic(entry);
      return entry;
    } finally {
      inflight.delete(fk);
    }
  })();

  inflight.set(fk, task);
  return task;
}

export async function ensureProductPics(
  brand: string,
  modelKeys: string[]
): Promise<void> {
  const unique = Array.from(
    new Set(modelKeys.map((k) => (k || "").trim()).filter(Boolean))
  );
  await Promise.all(unique.map((k) => ensureProductPic(brand, k)));
}
